import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { FaExpand as ExpandMoreIcon } from 'react-icons/fa';
import { CareerRecommendation } from '../types';

interface Props {
  recommendation: CareerRecommendation;
}

export const CareerResultCard: React.FC<Props> = ({ recommendation }) => {
  const {
    title,
    description,
    matchPercentage,
    reasonsForMatch,
    suggestedMajors,
    topColleges,
    careerPaths
  } = recommendation;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom color="primary">
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flexGrow: 1, mr: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={matchPercentage} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                }
              }}
            />
          </Box>
          <Typography variant="h6" color="primary">
            {matchPercentage}% Match
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {description}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Why This Matches You
          </Typography>
          <List>
            {reasonsForMatch.map((reason, index) => (
              <ListItem key={index}>
                <ListItemText primary={reason} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Suggested Majors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {suggestedMajors.map((major, index) => (
                  <Chip 
                    key={index} 
                    label={major} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Top Colleges
              </Typography>
              <List>
                {topColleges.map((college, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={college.name}
                      secondary={`${college.location} • ${college.specialization}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
          Career Paths & Roles
        </Typography>
        {careerPaths.map((path, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="medium">
                {path.role}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                {path.description}
              </Typography>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Requirements:
              </Typography>
              <List dense>
                {path.requirements.map((req, reqIndex) => (
                  <ListItem key={reqIndex}>
                    <ListItemText primary={req} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default CareerResultCard;
